import { IBuyBonusRequest, IGambleRequest, ISpinRequest } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { ServerRequests } from './dg/ServerRequests'
import { Model } from '@/model/Model'
import { ModulesManager } from '@modules/src/managers/ModulesManager'
import { SessionTelemetryModule } from '@modules/src/modules/SessionTelemetryModule'

export class BackendManager {
    private readonly _requests: ServerRequests
    private _model: Model

    public constructor(model: Model) {
        this._requests = new ServerRequests(model)
        this._model = model

        GameSignals.getInitResponse.connect(this.onGetInitResponse)
        GameSignals.getSpinResponse.connect(this.onGetSpinResponse)
        GameSignals.getBalanceResponse.connect(this.onGetBalanceResponse)
        GameSignals.backendMessage.connect(this.onBackendMessage)
        GameSignals.getBuyBonusResponse.connect(this.onGetBuyBonusResponse)
        ;(GameSignals as any).getGambleResponse.connect(this.onGetGambleResponse)
        ;(GameSignals as any).promoFreeSpinsDeclined.connect(this.onPromoFreeSpinsDeclined)
    }

    private onGetInitResponse = () => {
        this._requests.getInit()
    }

    private onGetSpinResponse = (data: ISpinRequest) => {
        this._requests.getSpin(data)
    }

    private onGetBalanceResponse = () => {
        try {
            this._requests.getBalance()
        } catch (error) {
            console.warn('[BackendManager] getBalance request failed, using local fallback balance', error)
            GameSignals.balanceUpdate.emit(this._model.balance)
        }
    }

    private onGetBuyBonusResponse = (data: IBuyBonusRequest) => {
        this._requests.getBuyBonus(data)
    }

    private onGetGambleResponse = (data: IGambleRequest) => {
        this._requests.getGamble(data)
    }

    private onPromoFreeSpinsDeclined = (data: any) => {
        console.log('[BackendManager] Promo free spins declined, calling cancel API', data)
        
        // Estrae bfs_record_id dal payload
        const bfsRecordId = 
            data?.promo_details?.bfs_record_id ?? 
            data?.next_round?.bfs_record_id ??
            data?.bfs_record_id
        
        if (bfsRecordId) {
            console.log('[BackendManager] Calling cancelPromoFreeSpins with bfs_record_id:', bfsRecordId)
            this.cancelPromoFreeSpins(bfsRecordId)
        } else {
            console.warn('[BackendManager] No bfs_record_id found in promo decline data, cannot cancel')
        }
    }

    private onBackendMessage = (message: any) => {
        switch (message.action) {
            case 'init':
                GameSignals.initResponse.emit(message.data)
                break

            case 'spin':
                GameSignals.spinResponse.emit(message.data)
                // Emit economy telemetry for round settlement
                this.emitSpinEconomyEvents(message.data)
                break

            case 'balance': {
                if (message.data && message.data.balance != null) {
                    GameSignals.balanceUpdate.emit(message.data.balance)
                    this._model.balance = message.data.balance
                }

                break
            }

            case 'terminate':
                this._requests.getTerminate()
                break

            case 'buyBonus':
                GameSignals.buyBonusResponse.emit(message.data)
                // Emit economy telemetry for bonus purchase
                this.emitBonusBuyEvent(message.data)
                break

            case 'gamble': {
                if (message.data?.player?.balance != null) {
                    GameSignals.balanceUpdate.emit(message.data.player.balance)
                    this._model.balance = Number(message.data.player.balance)
                }

                ;(GameSignals as any).gambleResponse.emit(message.data)
                break
            }

            case 'cancelPFS': {
                try {
                    console.log('[BackendManager] Cancel PFS response received:', message.data)
                    
                    // Controlla se ci sono errori nella risposta
                    if (message.data?.error || message.data?.error_number || message.data?.error_code) {
                        console.error('[BackendManager] Cancel PFS error:', {
                            error: message.data.error,
                            error_number: message.data.error_number,
                            error_code: message.data.error_code,
                            error_message: message.data.error_message,
                            full_response: message.data
                        })
                        // Non bloccare il flusso, emetti comunque il segnale
                    }
                    
                    // Aggiorna il balance se presente nella risposta
                    if (message.data && message.data.balance != null) {
                        GameSignals.balanceUpdate.emit(message.data.balance)
                        this._model.balance = message.data.balance
                    }
                    
                    // Emetti segnale per notificare che i promo sono stati cancellati
                    const gs: any = GameSignals
                    gs.promoDeclined?.emit(message.data)
                    
                    // Come nel vecchio framework: ricarica lo stato del gioco dopo 1 secondo
                    setTimeout(() => {
                        console.log('[BackendManager] Reloading game state after promo decline')
                        this._requests.getInit()
                    }, 1000)
                } catch (error) {
                    console.error('[BackendManager] Error processing cancel PFS response:', error, message.data)
                }
                
                break
            }

            case 'tournament': {
                try {
                    console.log('[BackendManager] Tournament response received:', message.data)
                    
                    // Salva i dati torneo nel model
                    if (message.data) {
                        this._model.tournamentData = message.data
                    }
                    
                    // Emetti segnale per aggiornare la UI
                    const gs: any = GameSignals
                    gs.tournamentDataReceived?.emit(message.data)
                } catch (error) {
                    console.error('[BackendManager] Error processing tournament response:', error, message.data)
                }
                
                break
            }
        }
    }

    public cancelPromoFreeSpins(bfsRecordId: number) {
        this._requests.cancelPromoFreeSpins(bfsRecordId)
    }

    public getTournamentLeaderboard(tournamentId: number) {
        this._requests.getTournamentLeaderboard(tournamentId)
    }

    public joinTournament(tournamentId: number) {
        this._requests.joinTournament(tournamentId)
    }

    /**
     * Get the telemetry module for emitting economy events
     */
    private getTelemetryModule(): SessionTelemetryModule | null {
        try {
            return ModulesManager.getModule('sessionTelemetry') as SessionTelemetryModule | null
        } catch {
            return null
        }
    }

    /**
     * Emit economy telemetry event
     */
    private emitEconomyEvent(eventType: string, payload: Record<string, any>): void {
        const telemetryModule = this.getTelemetryModule()
        if (!telemetryModule) return

        try {
            (telemetryModule as any).emitEconomyEvent(eventType, payload)
        } catch (error) {
            console.warn('[BackendManager] Failed to emit economy event:', error)
        }
    }

    /**
     * Emit round_settled and wager_accepted events when spin response is received
     */
    private emitSpinEconomyEvents(spinData: any): void {
        if (!spinData) return

        const roundId = spinData.round_id || spinData.id || `round-${Date.now()}`
        const betAmount = Number(spinData.bet_amount || spinData.wager || 0) * 100 // convert to minor units
        const payoutAmount = Number(spinData.win_currency || spinData.payout_amount || 0) * 100
        const valueType = spinData.value_type || 'real_money'
        const currency = spinData.currency || 'USD'
        const isFreeSpinRound = !!spinData.freeround || !!spinData.freebet
        const betKind = isFreeSpinRound ? 'free_spin' : 'base'

        // Emit round_settled event
        this.emitEconomyEvent('round_settled', {
            round_id: roundId,
            value_type: valueType,
            bet_amount_minor: betAmount,
            payout_amount_minor: payoutAmount,
            currency: currency,
            bet_kind: betKind,
            settle_duration_ms: spinData.settle_duration_ms || 0,
        })
    }

    /**
     * Emit wager_accepted event when spin request is made
     */
    public emitWagerAcceptedEvent(bet: number, roundId: string): void {
        const betAmount = Math.round(bet * 100) // convert to minor units
        this.emitEconomyEvent('wager_accepted', {
            round_id: roundId,
            value_type: 'real_money',
            bet_amount_minor: betAmount,
            currency: 'USD',
            bet_kind: 'base',
        })
    }

    /**
     * Emit bonus_buy_purchased event
     */
    private emitBonusBuyEvent(buyBonusData: any): void {
        if (!buyBonusData) return

        const roundId = buyBonusData.round_id || buyBonusData.id || `bonus-${Date.now()}`
        const costAmount = Number(buyBonusData.cost || buyBonusData.cost_minor || 0)

        this.emitEconomyEvent('bonus_buy_purchased', {
            round_id: roundId,
            value_type: 'real_money',
            cost_minor: costAmount,
            currency: 'USD',
            feature_kind: buyBonusData.feature_kind || 'bonus_buy',
        })
    }
}

